import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Stack,
  Link as MuiLink,
  useTheme,
  alpha,
  Card,
  CardContent,
  CardActions,
  CardActionArea,
  TextField,
  Alert,
} from '@mui/material';


import { Link, useLocation, useNavigate } from 'react-router-dom';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import TerminalIcon from '@mui/icons-material/Terminal';
import ApiIcon from '@mui/icons-material/Api';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import Groups2Icon from '@mui/icons-material/Groups2';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import GitHubIcon from '@mui/icons-material/GitHub';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MapIcon from '@mui/icons-material/Map';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useTranslation } from 'react-i18next';
import guardianLogo from '../../assets/guardian-ai-logo.webp';
import guardianLogoWhite from '../../assets/guardian-ai-logo-white.webp';
import platformPreview from '../../assets/nativetongue/edo-ai-mockup.png';
import { usePublicTeamPage, usePublicWorkInPublicConfig, useSubmitLead } from '@/hooks/useMarketing';
import { usePublicProductCatalog } from '@/hooks/useReferralSales';
import { useAuth } from '@/hooks/useAuth';
import { getIconByName } from '@/utils/iconMapper';
import { parseJsonObject } from '@/utils/json';

type ExternalLinkItem = {
  key: string;
  label: string;
  url?: string;
  icon: React.ReactNode;
};

const normalizeExternalUrl = (value?: string): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^(https?:\/\/|mailto:)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const extractYouTubeVideoId = (url?: string): string | undefined => {
  if (!url) return undefined;
  const normalizedUrl = normalizeExternalUrl(url);
  if (!normalizedUrl) return undefined;

  try {
    const parsed = new URL(normalizedUrl);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    const segments = parsed.pathname.split('/').filter(Boolean);

    if (host === 'youtu.be') {
      return segments[0];
    }

    if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
      if (parsed.pathname === '/watch') {
        return parsed.searchParams.get('v') || undefined;
      }
      if (segments[0] === 'live' && segments[1]) return segments[1];
      if (segments[0] === 'shorts' && segments[1]) return segments[1];
      if (segments[0] === 'embed' && segments[1]) return segments[1];
    }
  } catch {
    // Fall through to regex parsing for malformed but usable URLs.
  }

  const raw = normalizedUrl;
  const watchMatch = raw.match(/[?&]v=([^&]+)/i);
  if (watchMatch?.[1]) return watchMatch[1];
  const shortMatch = raw.match(/youtu\.be\/([^?&/]+)/i);
  if (shortMatch?.[1]) return shortMatch[1];
  const liveMatch = raw.match(/\/live\/([^?&/]+)/i);
  if (liveMatch?.[1]) return liveMatch[1];
  return undefined;
};

export const LandingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(['landing', 'common']);
  const { data: teamPageData } = usePublicTeamPage();
  const { data: workInPublic } = usePublicWorkInPublicConfig();
  const submitLead = useSubmitLead();
  const [workInPublicEmail, setWorkInPublicEmail] = useState('');
  const [workInPublicName, setWorkInPublicName] = useState('');
  const [workInPublicSuccess, setWorkInPublicSuccess] = useState('');
  const [workInPublicError, setWorkInPublicError] = useState('');
  const about = teamPageData?.about;
  const teamMembers = [...(teamPageData?.teamMembers || [])].sort(
    (a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0)
  );
  const aboutLinks: ExternalLinkItem[] = [
    { key: 'website', label: 'Website', url: normalizeExternalUrl(about?.websiteUrl), icon: <LanguageIcon fontSize="small" /> },
    { key: 'linkedin', label: 'LinkedIn', url: normalizeExternalUrl(about?.linkedinUrl), icon: <LinkedInIcon fontSize="small" /> },
    { key: 'x', label: 'X', url: normalizeExternalUrl(about?.xUrl), icon: <XIcon fontSize="small" /> },
    { key: 'github', label: 'GitHub', url: normalizeExternalUrl(about?.githubUrl), icon: <GitHubIcon fontSize="small" /> },
    { key: 'google-business', label: 'Google Business', url: normalizeExternalUrl(about?.googleBusinessUrl), icon: <StorefrontIcon fontSize="small" /> },
    { key: 'google-maps', label: 'Google Maps', url: normalizeExternalUrl(about?.googleMapsUrl), icon: <MapIcon fontSize="small" /> },
    { key: 'yelp', label: 'Yelp', url: normalizeExternalUrl(about?.yelpUrl), icon: <RateReviewIcon fontSize="small" /> },
  ];
  const visibleAboutLinks = aboutLinks.filter((link) => Boolean(link.url));
  const aboutSummary =
    about?.body?.trim() ||
    'Guardian AI is founded by Aikhomu Okoedion and built for a global market with security, trust, and infrastructure products.';
  const previewImage = about?.heroImageUrl || platformPreview;
  const teamSnapshot = teamMembers.slice(0, 3);
  const workInPublicUrl = normalizeExternalUrl(workInPublic?.youtubeUrl);
  const workInPublicVideoId = useMemo(() => extractYouTubeVideoId(workInPublicUrl), [workInPublicUrl]);
  const workInPublicThumbnailUrl = workInPublicVideoId
    ? `https://i.ytimg.com/vi/${workInPublicVideoId}/hqdefault.jpg`
    : undefined;
  const workInPublicStatusLabel =
    workInPublic?.sessionStatus === 'live'
      ? 'LIVE ON YOUTUBE'
      : workInPublic?.sessionStatus === 'vod'
        ? 'WATCH REPLAY ON YOUTUBE'
        : 'OPEN ON YOUTUBE';

  useEffect(() => {
    const shouldScrollToSection =
      location.pathname === '/work-in-public' || location.hash === '#work-in-public-section';
    if (!shouldScrollToSection) return;

    let attempts = 0;
    const interval = window.setInterval(() => {
      const section = document.getElementById('work-in-public-section');
      if (section) {
        section.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        window.clearInterval(interval);
      } else if (++attempts >= 30) {
        window.clearInterval(interval);
      }
    }, 100);

    return () => window.clearInterval(interval);
  }, [location.pathname, location.hash]);

  const handleWorkInPublicSubscribe = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!workInPublicEmail.trim()) return;

    try {
      setWorkInPublicError('');
      setWorkInPublicSuccess('');
      await submitLead.mutateAsync({
        email: workInPublicEmail.trim(),
        name: workInPublicName.trim() || undefined,
        source: 'work_in_public_notify',
      });
      setWorkInPublicSuccess('You are subscribed. We will email you when a new session is posted.');
      setWorkInPublicEmail('');
      setWorkInPublicName('');
    } catch (err: any) {
      setWorkInPublicError(err?.message || 'Subscription failed. Please try again.');
    }
  };

  const scheduledTimeLabel = useMemo(() => {
    const seconds = workInPublic?.scheduledFor?.seconds;
    if (seconds === undefined || seconds === null) return '';
    const sec = Number(seconds);
    if (!Number.isFinite(sec)) return '';
    const nanos = workInPublic?.scheduledFor?.nanos || 0;
    return new Date(sec * 1000 + Math.floor(nanos / 1_000_000)).toLocaleString();
  }, [workInPublic?.scheduledFor]);

  const { data: catalogData } = usePublicProductCatalog(1, 100);

  const productHighlights = useMemo(() => {
    const catalogProducts = catalogData?.products || [];

    interface ProductHighlightItem {
      key: string;
      icon: React.ReactElement;
      title: string;
      description: string;
      primaryLabel: string;
      primaryTo: string;
      secondaryLabel?: string;
      secondaryHref?: string;
      landingUrl?: string;
    }

    const highlights: ProductHighlightItem[] = catalogProducts
      .filter((p) => (p.category === 'infrastructure' || p.category === 'product' || p.category === 'marketplace') && p.isAvailableForSales)
      .map((p) => {
        const metadata = parseJsonObject(p.metadataJson);
        const planPath = `/subscription?plan=${p.frontendSlug}`;
        const registerWithPlan = `/register?product=${p.frontendSlug}&redirect=${encodeURIComponent(planPath)}`;
        const consolePath = `/console/${p.frontendSlug === 'distro-engine' ? 'distro-engine' : p.frontendSlug === 'caption-engine' ? 'caption-engine' : p.frontendSlug}`;

        let primaryTo = typeof metadata.primaryTo === 'string' && metadata.primaryTo.trim()
          ? metadata.primaryTo.trim()
          : p.frontendSlug.startsWith('/') ? p.frontendSlug : `/${p.frontendSlug}`;
        
        // Explicit overrides requested by user for specific catalog products
        if (p.name === 'Guardian CLI' || p.frontendSlug === 'guardian-cli') {
          primaryTo = '/cli-docs';
        } else if (p.name === 'Enterprise Security' || p.frontendSlug === 'enterprise-security') {
          primaryTo = '/rag-integrity-sentinel';
        } else if (p.name === 'Professional Services' || p.frontendSlug === 'professional-services') {
          primaryTo = '/services';
        }
        
        // falling back to the registration flow.
        const knownRoutes = [
          '/distro-engine',
          '/caption-engine',
          '/enterprise/vcn',
          '/enterprise/gateway-sniffer',
          '/enterprise/gatekeeperx',
          '/media-proof',
          '/rag-integrity-sentinel',
          '/infra-demo',
          '/cli-docs',
          '/native-tongue',
          '/sandbox',
          '/referral-program',
          '/whitepaper',
          '/services',
          '/family-care',
          '/governable-ai',
          '/showroom/fellowvia'
        ];
        if (!primaryTo.startsWith('http') && !knownRoutes.includes(primaryTo)) {
           primaryTo = isAuthenticated ? planPath : registerWithPlan;
        }

        return {
          key: p.frontendSlug,
          icon: getIconByName(metadata.icon || 'CloudQueue', { color: 'primary', sx: { fontSize: 42, mb: 1 } }),
          title: p.name,
          description: p.description,
          primaryLabel: metadata.primaryLabel || 'View Product',
          primaryTo: primaryTo,
          secondaryLabel: metadata.secondaryLabel,
          secondaryHref: metadata.secondaryHref,
          landingUrl: metadata.landingUrl || (metadata.externalApp ? (metadata.landingUrl || metadata.secondaryHref || metadata.primaryTo) : undefined),
        };
      });

    // Fallback card entries if not returned by catalog query
    if (!highlights.some(h => h.key === 'site-recovery')) {
      highlights.push({
        key: 'site-recovery',
        icon: getIconByName('Shield', { color: 'primary', sx: { fontSize: 42, mb: 1 } }),
        title: 'Guardian Site Recovery',
        description: 'Detect webshells and backdoors, correlate ingress vectors from server logs, review cryptographic diffs, and safely execute verified malware cleanup with rollback protection.',
        primaryLabel: 'Sign In to Workspace',
        primaryTo: 'https://siterecovery.guardianai.network/#/login',
        secondaryLabel: 'Get Started Free',
        secondaryHref: 'https://siterecovery.guardianai.network/#/register',
        landingUrl: 'https://siterecovery.guardianai.network/',
      });
    }

    if (!highlights.some(h => h.key === 'commerce-recovery')) {
      highlights.push({
        key: 'commerce-recovery',
        icon: getIconByName('Storefront', { color: 'primary', sx: { fontSize: 42, mb: 1 } }),
        title: 'Guardian Commerce Recovery',
        description: 'Automated Shopify chargeback defense, dispute evidence aggregation, and revenue recovery engine for high-volume e-commerce merchants.',
        primaryLabel: 'Open Shopify App',
        primaryTo: 'https://recovery.guardianai.network/',
        secondaryLabel: 'Merchant Guide',
        secondaryHref: 'https://recovery.guardianai.network/#/guide',
        landingUrl: 'https://recovery.guardianai.network/',
      });
    }

    if (!highlights.some(h => h.key === 'fellowvia')) {
      highlights.push({
        key: 'fellowvia',
        icon: getIconByName('MenuBook', { color: 'primary', sx: { fontSize: 42, mb: 1 } }),
        title: 'Fellowvia',
        description: 'Equip pastors and ministry leaders to publish daily devotionals and engage congregations through private, distraction-free SMS broadcasts.',
        primaryLabel: 'Staff Sign In',
        primaryTo: 'https://fellowvia.guardianai.network/sign-in',
        secondaryLabel: 'View Showroom',
        secondaryHref: '/showroom/fellowvia',
      });
    }

    if (!highlights.some(h => h.key === 'careconfirm')) {
      highlights.push({
        key: 'careconfirm',
        icon: getIconByName('ChildCare', { color: 'primary', sx: { fontSize: 42, mb: 1 } }),
        title: 'CareConfirm',
        description: 'Streamline daily childcare attendance, real-time booking rosters, automated parent confirmations, and billing compliance for daycare centers.',
        primaryLabel: 'Staff & Parent Sign In',
        primaryTo: 'https://careconfirm.guardianai.network/login',
        secondaryLabel: 'Learn More',
        secondaryHref: 'https://careconfirm.guardianai.network',
      });
    }

    // Always append professional services link if not present
    if (!highlights.some(h => h.key === 'professional-services')) {
      highlights.push({
        key: 'professional-services',
        icon: getIconByName('Groups2', { color: 'primary', sx: { fontSize: 42, mb: 1 } }),
        title: 'Professional Services',
        description: 'Security implementation, remediation support, and advisory services for scaling teams.',
        primaryLabel: 'View Services',
        primaryTo: '/services',
        secondaryLabel: undefined,
        secondaryHref: undefined,
      });
    }

    if (!highlights.some(h => h.key === 'family-care')) {
      highlights.push({
        key: 'family-care',
        icon: getIconByName('ChildCare', { color: 'primary', sx: { fontSize: 42, mb: 1 } }),
        title: 'Guardian AI Family Care',
        description: 'In-home child care in Calgary with online booking, secure payment, visit records, and Guardian AI receipts.',
        primaryLabel: 'View Family Care',
        primaryTo: '/family-care',
        secondaryLabel: undefined,
        secondaryHref: undefined,
      });
    }

    if (!highlights.some(h => h.key === 'governable-ai')) {
      highlights.push({
        key: 'governable-ai',
        icon: getIconByName('FactCheck', { color: 'primary', sx: { fontSize: 42, mb: 1 } }),
        title: 'Governable AI',
        description: 'A suite landing for AI governance templates, toolkit delivery, paid Workspace records, approval gates, redress, and audit-ready exports.',
        primaryLabel: 'View Governable AI',
        primaryTo: '/governable-ai',
        secondaryLabel: undefined,
        secondaryHref: undefined,
      });
    }

    return highlights;
  }, [catalogData, isAuthenticated]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section - Updated Text */}
      <Box
        sx={{
          pt: 15,
          pb: 8,
          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}> {/* Wider text area */}
              <Typography
                variant="h2"
                component="h1"
                sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}
              >
                {t('hero.title')}
              </Typography>
              <Typography
                variant="h5"
                sx={{ mb: 2, opacity: 0.9, fontSize: { xs: '1.2rem', md: '1.5rem' } }}
              >
                {t('hero.subtitle')}
              </Typography>
              {(() => {
                const features = t('hero.features', { returnObjects: true });
                if (Array.isArray(features)) {
                  return (
                    <Box component="ul" sx={{ mb: 4, pl: 3, opacity: 0.9, typography: 'h6', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                      {features.map((feature: any, index: number) => (
                        <Box component="li" key={index} sx={{ mb: 1 }}>
                          {feature}
                        </Box>
                      ))}
                    </Box>
                  );
                }
                return null;
              })()}
              {/* Buttons remain similar */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Get Started Button */}
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    '&:hover': { bgcolor: alpha('#fff', 0.9) },
                  }}
                >
                  {t('hero.get_started')}
                </Button>
                {/* AI Agent registration is currently disabled */}
                {/* <Button variant="contained" size="large" disabled sx={{ bgcolor: '#bdbdbd', color: '#666' }}>AI Agent Registration (Coming Soon)</Button> */}
                {/* Learn More Button */}
                <Button
                  component={Link}
                  to="/whitepaper" // Still relevant?
                  variant="outlined"
                  size="large"
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: alpha('#fff', 0.9),
                      bgcolor: alpha('#fff', 0.1),
                    },
                  }}
                >
                  {t('hero.learn_more')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              {/* Replace the entire parent Box with a responsive logo that fills the space */}
              <Box sx={{
                bgcolor: 'transparent',
                height: 300,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                p: 0,
                m: 0,
                overflow: 'hidden',
              }}>
                <img
                  src={theme.palette.primary.main === '#0A2B4C' ? guardianLogoWhite : guardianLogo}
                  alt="Guardian AI Logo"
                  decoding="async"
                  fetchPriority="high"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    maxHeight: 300,
                    maxWidth: '100%',
                    display: 'block',
                  }}
                  data-testid="GuardianAILogoHero"
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, mb: 6, boxShadow: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>
                What Guardian AI Is
              </Typography>
              <Typography variant="h4" sx={{ mt: 1, mb: 2, fontWeight: 700 }}>
                A digital-native security platform for consumers, teams, and developers
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 980, lineHeight: 1.8 }}>
                Guardian AI operates as a cloud software platform with product lines for fraud prevention,
                verification workflows, and secure deployment operations. We serve both B2C users and B2B
                organizations through subscription and platform usage models, with API-driven services for
                enterprise integrations.
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button component={Link} to="/team" variant="contained">
                  Meet the Team
                </Button>
                <Button component={Link} to="/contact" variant="outlined">
                  Contact Sales
                </Button>
                <Button component={Link} to="/cli-docs" variant="outlined">
                  Developer Docs
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                component="img"
                src={previewImage}
                alt="Guardian AI platform preview"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 1,
                }}
              />
            </Grid>

          </Grid>
        </Paper>

        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, mb: 6, boxShadow: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>
                Company Profile
              </Typography>
              <Typography variant="h4" sx={{ mt: 1, mb: 1.5, fontWeight: 700 }}>
                {about?.title || 'About Guardian AI'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 980, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                {aboutSummary}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip label="B2B SaaS" color="primary" variant="outlined" />
                <Chip label="B2C Protection" color="primary" variant="outlined" />
                <Chip label="API Platform" color="primary" variant="outlined" />
                <Chip label="CLI Tooling" color="primary" variant="outlined" />
              </Stack>
              {visibleAboutLinks.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                  {visibleAboutLinks.map((link) => (
                    <Button
                      key={link.key}
                      component="a"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      variant="outlined"
                      startIcon={link.icon}
                    >
                      {link.label}
                    </Button>
                  ))}
                </Stack>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button component={Link} to="/team" variant="contained">
                  Team & About
                </Button>
                <Button component={Link} to="/blog" variant="outlined">
                  Company Updates
                </Button>
                <Button component={Link} to="/contact" variant="outlined">
                  Contact
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Team Snapshot
                </Typography>
                {teamSnapshot.length > 0 ? (
                  <Stack spacing={1.5}>
                    {teamSnapshot.map((member) => (
                      <Box key={member.id}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {member.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {member.roleTitle}
                        </Typography>
                      </Box>
                    ))}
                    {teamMembers.length > teamSnapshot.length && (
                      <Typography variant="caption" color="text.secondary">
                        +{teamMembers.length - teamSnapshot.length} more on the Team page
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Team profiles, founder bio, and company social links are published on the Team page and managed from the admin dashboard.
                  </Typography>
                )}
                <MuiLink component={Link} to="/team" underline="hover" sx={{ mt: 2, display: 'inline-block' }}>
                  View full team
                </MuiLink>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Product Portfolio
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Products and services currently available across consumer, enterprise, and developer workflows.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {productHighlights.map((product) => (
            <Grid item xs={12} md={4} key={product.key}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea
                  onClick={() => {
                    const target = product.landingUrl || (product.primaryTo.startsWith('http') ? product.primaryTo : product.secondaryHref) || product.primaryTo;
                    if (target) {
                      if (target.startsWith('http')) {
                        window.open(target, '_blank');
                      } else {
                        navigate(target);
                      }
                    }
                  }}
                  sx={{ flexGrow: 1, alignItems: 'stretch', display: 'flex', flexDirection: 'column', textAlign: 'left', p: 0 }}
                >
                  <CardContent sx={{ width: '100%', flexGrow: 1 }}>
                    {product.icon}
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                      {product.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {product.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
                <CardActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2, mt: 'auto' }}>
                  {product.primaryTo.startsWith('http') ? (
                    <Button
                      component="a"
                      href={product.primaryTo}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      variant="contained"
                    >
                      {product.primaryLabel}
                    </Button>
                  ) : (
                    <Button component={Link} to={product.primaryTo} size="small" variant="contained">
                      {product.primaryLabel}
                    </Button>
                  )}
                  {product.secondaryHref && product.secondaryLabel && (
                    product.secondaryHref.startsWith('http') ? (
                      <Button
                        component="a"
                        href={product.secondaryHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="outlined"
                      >
                        {product.secondaryLabel}
                      </Button>
                    ) : (
                      <Button
                        component={Link}
                        to={product.secondaryHref}
                        size="small"
                        variant="outlined"
                      >
                        {product.secondaryLabel}
                      </Button>
                    )
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper id="work-in-public-section" sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, mt: 6, boxShadow: 2, scrollMarginTop: '96px' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>
                Work in Public
              </Typography>
              <Typography variant="h4" sx={{ mt: 1, mb: 1.5, fontWeight: 700 }}>
                Join Our Live Build Sessions
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                We run public engineering sessions on YouTube to show real implementation work.
                Subscribe below to get notified when a new live or scheduled session is posted.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip
                  icon={<LiveTvIcon />}
                  color={workInPublic?.sessionStatus === 'live' ? 'error' : 'primary'}
                  label={
                    workInPublic?.sessionStatus === 'live'
                      ? 'Happening now'
                      : workInPublic?.sessionStatus === 'vod'
                        ? 'Replay available'
                        : 'Scheduled'
                  }
                />
                {scheduledTimeLabel && (
                  <Chip label={`Time: ${scheduledTimeLabel}`} variant="outlined" />
                )}
              </Stack>

              {workInPublic?.focusArea && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Current Focus:</strong> {workInPublic.focusArea}
                </Typography>
              )}

              {workInPublicUrl ? (
                <Paper
                  sx={{
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 2,
                    cursor: 'pointer',
                    aspectRatio: '16/9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 4,
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.01)' },
                  }}
                  onClick={() => window.open(workInPublicUrl, '_blank', 'noopener,noreferrer')}
                >
                  {workInPublicThumbnailUrl ? (
                    <Box
                      component="img"
                      src={workInPublicThumbnailUrl}
                      alt="YouTube session preview"
                      loading="lazy"
                      decoding="async"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.900',
                      }}
                    >
                      <LiveTvIcon sx={{ color: 'common.white', fontSize: 56 }} />
                    </Box>
                  )}

                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.34)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlayCircleOutlineIcon sx={{ fontSize: { xs: 64, md: 78 }, color: '#fff' }} />
                  </Box>

                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 12,
                      right: 12,
                      backgroundColor: 'rgba(0, 0, 0, 0.72)',
                      color: '#fff',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold">
                      {workInPublicStatusLabel}
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Session link will appear here once an admin posts a live, scheduled, or replay URL.
                  </Typography>
                </Paper>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  component="a"
                  href={workInPublicUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={!workInPublicUrl}
                >
                  {workInPublic?.sessionStatus === 'live'
                    ? 'Watch Live on YouTube'
                    : workInPublic?.sessionStatus === 'vod'
                      ? 'Watch Replay on YouTube'
                      : 'Open YouTube Session'}
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Get Session Alerts
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter your email and we will notify you when a new session link is posted.
                </Typography>

                <Box component="form" onSubmit={handleWorkInPublicSubscribe}>
                  <Stack spacing={1.5}>
                    <TextField
                      label="Name (optional)"
                      value={workInPublicName}
                      onChange={(e) => setWorkInPublicName(e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Email"
                      type="email"
                      value={workInPublicEmail}
                      onChange={(e) => setWorkInPublicEmail(e.target.value)}
                      required
                      fullWidth
                      size="small"
                    />
                    <Button type="submit" variant="contained" disabled={submitLead.isPending}>
                      {submitLead.isPending ? 'Subscribing...' : 'Notify Me'}
                    </Button>
                  </Stack>
                </Box>

                {workInPublicSuccess && <Alert severity="success" sx={{ mt: 2 }}>{workInPublicSuccess}</Alert>}
                {workInPublicError && <Alert severity="error" sx={{ mt: 2 }}>{workInPublicError}</Alert>}
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Box sx={{ py: 8, bgcolor: 'alternate.main' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" sx={{ mb: 2, fontWeight: 700 }}>
            Digital-Native Business Model
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 5, maxWidth: 920, mx: 'auto' }}>
            Guardian AI combines B2C protection experiences, B2B SaaS services, and developer tooling under a single cloud platform strategy.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <BusinessCenterIcon color="primary" sx={{ mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  B2B SaaS
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Security workflows and enterprise features for organizations operating across regions.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <VerifiedUserIcon color="primary" sx={{ mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  B2C Protection
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Consumer-facing trust and fraud protection products with practical, guided remediation.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <TerminalIcon color="primary" sx={{ mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Developer Platform
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CLI and APIs for teams that need reliable build, deploy, and security operations.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, boxShadow: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Groups2Icon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Team & Leadership
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, maxWidth: 980 }}>
            {aboutSummary}
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button component={Link} to="/team" variant="contained">
              View Team Page
            </Button>
            <Button component={Link} to="/blog" variant="outlined">
              Read Company Updates
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* New Problem Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 600 }}>
          {t('problems.title')}
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: '800px', mx: 'auto' }}>
          {t('problems.subtitle')}
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
            <ReportProblemIcon sx={{ fontSize: 50, color: theme.palette.error.main, mb: 1 }} />
            <Typography variant="h6">{t('problems.transaction_fraud.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('problems.transaction_fraud.description')}</Typography>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
            <FindInPageIcon sx={{ fontSize: 50, color: theme.palette.warning.main, mb: 1 }} />
            <Typography variant="h6">{t('problems.online_scams.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('problems.online_scams.description')}</Typography>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
            <VerifiedUserIcon sx={{ fontSize: 50, color: theme.palette.info.main, mb: 1 }} />
            <Typography variant="h6">{t('problems.reputation_damage.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('problems.reputation_damage.description')}</Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Core Pillars Section - New */}
      <Box sx={{ py: 8, bgcolor: 'alternate.main' /* Use alternating bg color */ }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 2, fontWeight: 600 }}
          >
            {t('toolkit.title')}
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: '800px', mx: 'auto' }}>
            {t('toolkit.subtitle')}
          </Typography>
          <Grid container spacing={4} alignItems="stretch"> {/* Add alignItems stretch */}

            {/* Pillar 1: Scan & Verify */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <FindInPageIcon color="primary" sx={{ fontSize: 50 }} />
                  </Box>
                  <Typography variant="h5" component="div" gutterBottom align="center">
                    {t('toolkit.scan_verify.title')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t('toolkit.scan_verify.description')}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button size="small" component={Link} to="/register?product=fraud-prevention">{t('common:learn_more')}</Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Pillar 2: Report & Recover */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <ReportProblemIcon color="error" sx={{ fontSize: 50 }} />
                  </Box>
                  <Typography variant="h5" component="div" gutterBottom align="center">
                    {t('toolkit.report_recover.title')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t('toolkit.report_recover.description')}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button size="small" component={Link} to="/register?product=fraud-prevention">{t('common:learn_more')}</Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Pillar 3: Defend & Restore */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <VerifiedUserIcon color="success" sx={{ fontSize: 50 }} />
                  </Box>
                  <Typography variant="h5" component="div" gutterBottom align="center">
                    {t('toolkit.defend_restore.title')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t('toolkit.defend_restore.description')}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button size="small" component={Link} to="/register?product=fraud-prevention">{t('common:learn_more')}</Button>
                </CardActions>
              </Card>
            </Grid>

          </Grid>
          {/* Optional: Enterprise Teaser */}
          <Box sx={{ textAlign: 'center', mt: 8, pt: 4, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="h5" gutterBottom>{t('toolkit.enterprise_teaser.title')}</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('toolkit.enterprise_teaser.description')}
            </Typography>
            <Button variant="outlined" component={Link} to="/rag-integrity-sentinel">{t('toolkit.enterprise_teaser.explore')}</Button>
          </Box>
        </Container>
      </Box>

      {/* How It Works Section - New */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ mb: 2, fontWeight: 600 }}>{t('getting_started.title')}</Typography>
        <Grid container spacing={4} justifyContent="center" sx={{ mt: 4 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h5" color="primary">{t('getting_started.step1.title')}</Typography>
            <Typography variant="body1" color="text.secondary">{t('getting_started.step1.description')}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h5" color="primary">{t('getting_started.step2.title')}</Typography>
            <Typography variant="body1" color="text.secondary">{t('getting_started.step2.description')}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h5" color="primary">{t('getting_started.step3.title')}</Typography>
            <Typography variant="body1" color="text.secondary">{t('getting_started.step3.description')}</Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Call to Action Section - Updated Text */}
      <Box
        sx={{
          py: 8,
          bgcolor: theme.palette.grey[100], // Slightly different color for final CTA
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            {t('cta.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {t('cta.description')}
          </Typography>
          <Button
            component={Link}
            to="/register"
            variant="contained"
            size="large"
            sx={{ mt: 2, px: 5, py: 1.5 }} // Make button larger
          >
            {t('cta.button')}
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          mt: 'auto',
          backgroundColor: theme.palette.grey[900],
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} sm={4}>
              <Typography variant="h6" gutterBottom>
                Guardian AI
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Empowering individuals and organizations to detect, report, and recover from fraud.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h6" gutterBottom>
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link to="/register" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>
                  Get Started
                </Link>
                <Link to="/whitepaper" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>
                  Whitepaper
                </Link>
                <Link to="/media-proof" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>
                  Guardian Media Proof
                </Link>
                <Link to="/team" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>
                  Team & About
                </Link>
                <Link to="/cli-docs" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>
                  CLI Docs
                </Link>
                <Link to="/native-tongue" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>
                  Native Tongue Initiative
                </Link>
                <Link to="/enterprise/gatekeeperx" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>
                  Enterprise Solutions
                </Link>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h6" gutterBottom>
                Legal
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>
                  Terms of Service
                </Link>
                <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>
                  Privacy Policy
                </Link>
                <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>
                  Contact Us
                </Link>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'rgba(255,255,255,0.2)', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              © 2025–{new Date().getFullYear()} Guardian AI Inc. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
